import {
  toWSDate,
  WSElement,
  WSEmptyState,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction,
  WSPanel,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayeeEligibilityRequirement,
  PayeeEngagementStatus,
  PayeeIdentitySource,
  PayeeStatus,
  PayerLinkRequestStatus
} from "@wingspanhq/wingspan-api-sdk";
import React, { useState } from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";
import {
  queriesAccounts,
  queriesPayeeEligibilityRequirements,
  queriesPayeeEngagements,
  queriesPayees
} from "@query";
import { EngagementsTable } from "./EngagementsTable";
import { useSession } from "@hooks/useSession";
import { Tabs } from "../../../../components/Tabs";
import { WSQueries } from "../../../../query/WSQuery";
import { useGoTo } from "../../../../shared/utils/useGoTo";
import { ContractorInformation } from "../../components/ContractorInformation";
import { InternalNotesWidget } from "../../components/InternalNotesWidget";
import { selectorAccountDisplayName } from "../../../../shared/selectors/selectorAccountDisplayName";
import { selectorPayeeDisplayName } from "../../../../shared/selectors/selectorPayeeDisplayName";
import {
  buildPayeeDetailsPath,
  buildPayeeEditTaxInfoPath,
  PAYEES_ARCHIVED_PATH,
  PAYEES_DETAILS_ROUTE,
  PAYEES_LIST_PATH
} from "../../utils";
import { PayeeDetailsAlerts } from "./PayeeDetailsAlerts";
import { UnavailableOrgWidget } from "./UnavailableOrgWidget";
import { usePayeeRowActions } from "./usePayeeRowActions";
import { useXWingspanPayerId } from "./useXWingspanPayerId";

// ============================================================================
// v3 migration — accepted breakage (directive: "not a single v1 endpoint may be
// called on the contractor-details screen"; user-confirmed 2026-07-22).
//
// Everything this screen READS (`getPayee`, org `getAccount`) and the header
// WRITES (edit / archive / restore / invite / internal notes) is on v3. The
// reads + widgets commented out below hit v1 endpoints whose data has NO usable
// v3 source today:
//   - Requirements tab  → v1 GET /payments/requirement fed into
//                         PayerPayeeDetailsRequirements; the v3 twin
//                         (payee-eligibility-requirements) 403s at runtime and
//                         is a re-modelled instance/template split the v1-shaped
//                         component can't consume.
//   - ESignatureWidget  → v1 /signed-documents + /requirement-definition (no v3).
//   - CustomFieldsWidget→ v1 /payments/custom-fields (v3 GET /custom-fields 501).
//   - TaxFilingWidget   → v1 /search/tax-form-submission-row (no v3 search-row
//                         with the derived filing status).
//   - RecentPayables    → v1 /payments/payable + /payroll-settings (payroll
//                         settings drive the status column and have no v3 twin).
//   - DeductionsWidget  → v1 /payments/collaborator-deduction (no v3).
//   - DocumentsSection  → v1 /files/member/private (no v3 files registry).
//   - PayeeIntegration  → v1 /integrations/quickbooks/* reads (no v3 read for the
//                         QBO vendor/expense mapping on the payee).
//   - AutopayWidget     → v1 /user/client/:id for the payer's default payment
//                         method; the widget renders nothing without it.
//   - Resource history  → v1 GET /v2/resource-events (v3 audit-log not wired).
//
// Each removed panel already degraded to empty / non-functional on a v3 payee
// (its data never resolved), so commenting them out is not a functional
// regression — it only stops the dead v1 traffic. Preserved as comments for
// re-enablement once a v3 source ships. The app-wide experiments/feature-flags
// dependency reached via useXWingspanPayerId was also removed at the source
// (see useXWingspanExpansion). See V3_MIGRATION_TRACKER.md · /v3/payments/payees.
// ============================================================================

export const RoutePayeeDetails: React.FC<
  RouteComponentProps<{
    payeeId: string;
  }>
> = ({ match, history, location }) => {
  const goTo = useGoTo();
  const { payeeId } = match.params;

  const organizationId = useXWingspanPayerId();

  const isOrgContext = !!organizationId;
  const getPayeeActions = usePayeeRowActions();

  const { personId } = useSession();
  const queryPayee = queriesPayees.getPayee.useQuery({
    payeeId,
    xWingspanAccount: organizationId
  });
  // Org breadcrumb name (org-expansion context only). v3 read: getAccount →
  // selectorAccountDisplayName (was v1 GET /organization/user/:id).
  const queryOrganization = queriesAccounts.getAccount.useQuery(
    { accountId: organizationId! },
    { enabled: isOrgContext && organizationId !== personId }
  );

  // Assignments (payee-engagements) + their eligibility requirements, on v3.
  // Unblocked by WIN-15176 (paymentsEligibility now on the projection) and
  // WIN-15163 (payee-eligibility-requirements 403 resolved).
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const queryEngagements =
    queriesPayeeEngagements.listPayeeEngagements.useQuery({
      payeeId,
      xWingspanAccount: organizationId
    });
  const queryEligibilityRequirements =
    queriesPayeeEligibilityRequirements.listPayeeEligibilityRequirements.useQuery(
      { filter: { payeeId: { eq: payeeId } } }
    );

  return (
    <WSPage>
      <WSQueries
        queries={{
          queryPayee
        }}
      >
        {({ queryPayeeData }) => {
          const secondaryLoading =
            queryEngagements.isLoading ||
            queryEligibilityRequirements.isLoading;
          const engagements = queryEngagements.data?.data ?? [];
          const requirements = queryEligibilityRequirements.data?.data ?? [];
          // Group eligibility requirements by engagement for the per-row
          // "X of Y complete" count (v3 `PayeeEngagement` carries only the
          // `requirementIds` + `areAllRequirementsComplete` rollup).
          const requirementsByEngagementId = requirements.reduce<
            Record<string, PayeeEligibilityRequirement[]>
          >((acc, requirement) => {
            if (requirement.engagementId) {
              (acc[requirement.engagementId] ??= []).push(requirement);
            }
            return acc;
          }, {});
          const activeEngagements = engagements.filter(
            eng =>
              eng.status === PayeeEngagementStatus.CREATED ||
              eng.status === PayeeEngagementStatus.ACTIVATED
          );
          const archivedEngagements = engagements.filter(
            eng =>
              eng.status === PayeeEngagementStatus.SUSPENDED ||
              eng.status === PayeeEngagementStatus.TERMINATED
          );

          const payeeName = selectorPayeeDisplayName(queryPayeeData);

          const isInactive = queryPayeeData.status === PayeeStatus.DEACTIVATED;

          // `identitySource` is the correct ownership axis for payer-provided
          // vs contractor-shared: a `PayeeOwned` identity is contractor-shared
          // and the payer cannot edit it, while `PayerProvided` is "provided by
          // you" and editable.
          const isPayeeSharedInfo =
            queryPayeeData.identitySource === PayeeIdentitySource.PAYEE_OWNED;

          const headerActions: WSPageToolbarAction[] = getPayeeActions({
            payee: {
              payeeId,
              email: queryPayeeData.email,
              internalNotes: queryPayeeData.internalNotes ?? undefined,
              isInactive,
              isPending:
                queryPayeeData.linkRequestStatus ===
                PayerLinkRequestStatus.PENDING,
              isPayeeProvidedInfo: isPayeeSharedInfo
            },
            backPath: location.pathname
          });

          const organizationName = queryOrganization.data
            ? selectorAccountDisplayName(queryOrganization.data)
            : undefined;

          return (
            <>
              <WSPageToolbar
                breadcrumbs={[
                  {
                    label: "Contractors",
                    onClick: () => {
                      history.push(PAYEES_LIST_PATH);
                    }
                  },
                  ...(organizationName
                    ? [
                        {
                          label: organizationName,
                          onClick: () => {
                            history.push(
                              PAYEES_LIST_PATH +
                                `?payerId%5B0%5D=${organizationId}`
                            );
                          }
                        }
                      ]
                    : []),
                  ...(isInactive
                    ? [
                        {
                          label: "Archived contractors",
                          onClick: () => {
                            history.push(PAYEES_ARCHIVED_PATH);
                          }
                        }
                      ]
                    : []),
                  { label: payeeName }
                ]}
                title={payeeName}
                titlePill={
                  isInactive
                    ? {
                        theme: "neutral",
                        text: "Archived",
                        icon: false
                      }
                    : queryPayeeData.linkRequestStatus ===
                        PayerLinkRequestStatus.LINKED
                      ? { theme: "success", text: "Signed-up", icon: false }
                      : {
                          theme: "warning",
                          text: "Not signed-up",
                          icon: false
                        }
                }
                titleAfter={
                  <WSText.ParagraphXs color="gray500">
                    Last modified{" "}
                    {toWSDate(queryPayeeData.events?.updatedAt).formattedValue}
                  </WSText.ParagraphXs>
                }
                actions={headerActions}
              />
              <WSList gap="L">
                <PayeeDetailsAlerts payeeId={payeeId} />

                <WSGrid gutter="XL">
                  <WSGrid.Item span={{ m: "8" }}>
                    <Tabs
                      mb="L"
                      tabs={[
                        {
                          label: "Summary",
                          path: buildPayeeDetailsPath(payeeId)
                        }
                        // v3-migration accepted breakage: the Requirements tab
                        // reads v1 /payments/requirement; its pill state derived
                        // from requirement completion. Commented out with the
                        // requirements route below.
                        // {
                        //   label: "Requirements",
                        //   pill: {
                        //     icon: true,
                        //     theme: isAllRequirementsComplete
                        //       ? "success"
                        //       : "warning"
                        //   },
                        //   path: buildPayeeDetailsRequirementsPath(payeeId)
                        // }
                      ]}
                    />

                    <Switch>
                      {/*
                        v3-migration accepted breakage: the Requirements route
                        (PayerPayeeDetailsRequirements) is fed by the v1
                        requirements reads; the v3 payee-eligibility-requirements
                        twin 403s and is a re-modelled shape the v1-typed
                        component can't consume. Commented out with the tab above.

                        <Route
                          path={PAYEES_DETAILS_REQUIREMENTS_ROUTE}
                          render={() => (
                            <WSElement shimmer={tertiaryLoading}>
                              <PayerPayeeDetailsRequirements
                                requirements={requirements}
                                hasEligibleEngagements={hasEligibleEngagements}
                                noActions={isOrgContext}
                                archivedRequirements={queryArchivedRequirements.data || []}
                                contextType={RequirementContextType.Payee}
                                status={isInactive ? PayerPayeeStatus.Inactive : PayerPayeeStatus.Active}
                                goToRequirement={requirement =>
                                  history.push(
                                    `/member/payees/${payeeId}/requirement/${requirement.requirementId}`
                                  )
                                }
                              />
                            </WSElement>
                          )}
                        />
                      */}
                      <Route
                        path={PAYEES_DETAILS_ROUTE}
                        render={() => (
                          <WSList gap="XL">
                            <WSElement>
                              <WSSectionToolbar
                                pill={{
                                  text: isPayeeSharedInfo
                                    ? "Contractor shared"
                                    : "Provided by you",
                                  theme: isPayeeSharedInfo ? "blue" : "neutral"
                                }}
                                title="Contractor information"
                                button={
                                  isPayeeSharedInfo
                                    ? undefined
                                    : {
                                        label: "Edit",
                                        onClick: () => {
                                          goTo(
                                            buildPayeeEditTaxInfoPath(payeeId)
                                          );
                                        }
                                      }
                                }
                              />
                              <WSPanel mt="S">
                                <WSList gap="2XL">
                                  <ContractorInformation
                                    payeeId={payeeId}
                                    editable
                                  />
                                </WSList>
                              </WSPanel>
                            </WSElement>
                            <WSList gap="L">
                              {isInactive ? null : (
                                <WSElement shimmer={secondaryLoading}>
                                  <WSSectionToolbar
                                    title="Assignments"
                                    mb="M"
                                  />
                                  {activeEngagements.length ? (
                                    <EngagementsTable
                                      engagements={activeEngagements}
                                      requirementsByEngagementId={
                                        requirementsByEngagementId
                                      }
                                      payee={queryPayeeData}
                                    />
                                  ) : (
                                    <WSEmptyState
                                      type="tasks"
                                      title="No assignments active"
                                      description="Once an assignment is added and active, it will appear here."
                                      hasBorder
                                      orientation="horizontal"
                                    />
                                  )}
                                </WSElement>
                              )}
                              {archivedEngagements.length ? (
                                <WSElement>
                                  <WSSectionToolbar
                                    title={
                                      showArchived || isInactive
                                        ? "Archived assignments"
                                        : ""
                                    }
                                    mb="M"
                                    buttons={
                                      isInactive
                                        ? undefined
                                        : [
                                            {
                                              label: showArchived
                                                ? `Hide archived (${archivedEngagements.length})`
                                                : `Show archived (${archivedEngagements.length})`,
                                              onClick: () => {
                                                setShowArchived(v => !v);
                                              }
                                            }
                                          ]
                                    }
                                  />
                                  {isInactive || showArchived ? (
                                    <EngagementsTable
                                      engagements={archivedEngagements}
                                      requirementsByEngagementId={
                                        requirementsByEngagementId
                                      }
                                      payee={queryPayeeData}
                                    />
                                  ) : null}
                                </WSElement>
                              ) : null}
                            </WSList>
                          </WSList>
                        )}
                      />
                    </Switch>
                  </WSGrid.Item>
                  <WSGrid.Item span={{ m: "4" }}>
                    <WSList gap="XL">
                      <InternalNotesWidget payeeId={payeeId} />

                      {/*
                        v3-migration accepted breakage — commented out to keep
                        this screen off v1. Each widget's data has no usable v3
                        source today (see the banner at the top of this file):

                        {hasScope("payments.payable")
                          ? isOrgContext
                            ? null
                            : <RecentPayablesWidget payeeId={payeeId} />
                          : null}

                        <CustomFieldsWidget payeeId={payeeId} isInactive={isInactive} />

                        <TaxFilingWidget payeeId={payeeId} />

                        {isOrgContext ? null : (
                          <WSElement shimmer={secondaryLoading}>
                            <ESignatureWidget payeeId={payeeId} engagements={engagements} />
                          </WSElement>
                        )}

                        <DocumentsSection
                          isInactive={isInactive}
                          payeeId={payeeId}
                          payerId={userId}
                          type={DocumentsSectionType.Collaborator}
                        />

                        {isOrgContext ? null : (
                          <DeductionsWidget
                            clientId={userId}
                            memberId={payeeId}
                            onRowClick={row => {
                              history.push(
                                `/member/invoices/payables/deductions/scheduled/${row.deductionId}`
                              );
                            }}
                            onShowAllClick={() => {
                              history.push(`/member/invoices/payables/deductions/scheduled`);
                            }}
                          />
                        )}

                        <ResourceHistory
                          resources={historyResources}
                          onViewAll={() => { history.push(buildPayeeHistoryPath(payeeId)); }}
                        />

                        <AutopayWidget isInactive={isInactive} payeeId={payeeId} />

                        {isOrgContext ? null : (
                          <PayeeIntegrationWidget
                            isInactive={isInactive}
                            payeeId={payeeId}
                            vendorId={queryPayeeData.quickbooksVendorId ?? undefined}
                            expenseAccountId={undefined}
                          />
                        )}
                      */}

                      {isOrgContext ? (
                        <>
                          <UnavailableOrgWidget title="Recent payables" />
                          <UnavailableOrgWidget title="Scheduled deductions" />
                          <UnavailableOrgWidget title="Integration mappings" />
                        </>
                      ) : null}
                    </WSList>
                  </WSGrid.Item>
                </WSGrid>
              </WSList>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
